import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getMyOrders, getMyConsultations } from '../../services/accountApi';
import { PLANET_EMOJI } from '../../utils/astrology';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export default function AccountDashboard() {
  const { user, profile } = useAuth();
  const [orders, setOrders] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const load = async () => {
      const [o, c] = await Promise.all([
        getMyOrders(user.id),
        getMyConsultations(user.id),
      ]);
      setOrders(o.data);
      setConsultations(c.data);
      setLoading(false);
    };
    load();
  }, [user]);

  if (loading) return <div className="account-loading">Loading…</div>;

  const totalSpent = orders
    .filter((o) => o.payment_status === 'paid')
    .reduce((sum, o) => sum + Number(o.final_amount || 0), 0);

  return (
    <div>
      {/* Your planet */}
      {profile?.planet ? (
        <div className="planet-banner">
          <div className="planet-banner-icon">
            {PLANET_EMOJI[profile.planet] || '💎'}
          </div>
          <div>
            <p className="planet-banner-label">Your Ruling Planet</p>
            <h2>{profile.planet}</h2>
            <p className="planet-banner-sub">
              Birth number {profile.birth_number} · based on your date of birth
            </p>
          </div>
          <Link to="/shop" className="account-btn-sm">
            Shop {profile.planet} stones
          </Link>
        </div>
      ) : (
        <div className="planet-banner empty">
          <div className="planet-banner-icon">✦</div>
          <div>
            <h2>Discover your ruling planet</h2>
            <p className="planet-banner-sub">
              Add your date of birth and we'll calculate the gemstones aligned
              to you.
            </p>
          </div>
          <Link to="/account/profile" className="account-btn-sm">
            Add birth date
          </Link>
        </div>
      )}

      {/* Stats */}
      <div className="account-stats">
        <div className="account-stat">
          <span className="stat-num">{orders.length}</span>
          <span className="stat-label">Orders</span>
        </div>
        <div className="account-stat">
          <span className="stat-num">{inr(totalSpent)}</span>
          <span className="stat-label">Total Spent</span>
        </div>
        <div className="account-stat">
          <span className="stat-num">{consultations.length}</span>
          <span className="stat-label">Consultations</span>
        </div>
      </div>

      {/* Recent orders */}
      <div className="account-section">
        <div className="account-section-head">
          <h3>Recent Orders</h3>
          {orders.length > 0 && <Link to="/account/orders">View all →</Link>}
        </div>

        {orders.length === 0 ? (
          <div className="account-empty">
            <p>You haven't placed any orders yet.</p>
            <Link to="/shop" className="account-btn-sm">
              Browse the collection
            </Link>
          </div>
        ) : (
          <ul className="mini-list">
            {orders.slice(0, 3).map((o) => (
              <li key={o.id}>
                <div>
                  <strong>{o.order_number}</strong>
                  <span className="mini-sub">
                    {o.created_at
                      ? new Date(o.created_at).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })
                      : ''}
                  </span>
                </div>
                <div className="mini-right">
                  <span>{inr(o.final_amount)}</span>
                  <span className={`chip chip-${o.order_status}`}>
                    {o.order_status}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
